/**
 * IS A ROUTE ERROR BOUNDARY ON SCREEN? (#13956.)
 *
 * The AI chat FAB and the floating recording bar are mounted in the root
 * layout, ABOVE the router — that is what lets the bar hold a `MediaStream`
 * across a client-side navigation, and what lets the chat panel survive one.
 * A segment's `error.tsx` replaces only the `children` subtree beneath them,
 * so when a route throws, neither piece of chrome notices: the app goes on
 * offering to record a meeting and to ask a question about a page that did
 * not load.
 *
 * Neither consumer can be told by a prop (nothing connects them to the
 * boundary), by a context (they are above the provider), or by a pathname —
 * an errored `/meetings` has exactly the same pathname as a healthy one, and
 * that is the whole reason `shouldHideAiChat` grew an `isSurfaceSuppressed`
 * argument in the first place: "a posture struck inside a route rather than a
 * route".
 *
 * So the boundary PUBLISHES and the chrome SUBSCRIBES, through the same
 * module-store shape `src/lib/notetaker/meeting-page-context.ts` uses for the
 * same stated reason.
 *
 * ## Ref-counted, per segment
 *
 * Route segments nest: `/meetings` and `/meetings/[id]` can both carry an
 * `error.tsx`, and React can have both mounted at once. A plain boolean would
 * let the inner boundary's unmount clear the outer one's flag and put the
 * chrome straight back on top of a card that never went away. Counting per
 * segment also survives the less obvious case of two boundaries sharing a
 * segment name, which a `Set` would silently collapse.
 *
 * ## Client-safe
 *
 * No `server-only`, no Prisma, no DOM. The publisher is a client component;
 * the subscribers read it through `useSyncExternalStore`.
 */

/** Segment identifier -> how many boundaries for it are currently mounted. */
const mountedBySegment = new Map<string, number>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function subscribeToRouteErrorPosture(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** `true` while at least one boundary that suppresses chrome is mounted. */
export function getRouteErrorActive(): boolean {
  return mountedBySegment.size > 0
}

/**
 * The server snapshot, which is always `false`.
 *
 * A server render has no boundary mounted, and returning anything else would
 * make `useSyncExternalStore` hydrate chrome the server never drew. The
 * consumers additionally latch on `hasHydrated` for the window where the
 * boundary's effect has already run but the streamed sidecar has not yet
 * hydrated — see `AiChatFab`.
 */
export function getServerRouteErrorActive(): boolean {
  return false
}

export function publishRouteErrorActive(segment: string): void {
  const wasActive = getRouteErrorActive()
  mountedBySegment.set(segment, (mountedBySegment.get(segment) ?? 0) + 1)
  // Only the BOOLEAN is observable, so a second boundary arriving while one
  // is already up changes nothing — and every emit here re-renders the whole
  // of the root layout's floating chrome.
  if (!wasActive) emit()
}

export function clearRouteErrorActive(segment: string): void {
  const mounted = mountedBySegment.get(segment)
  // A clear for a segment that was never published is a no-op rather than a
  // debt: letting the count go negative would swallow the NEXT boundary's
  // publish, which is a hidden card with visible chrome.
  if (mounted === undefined) return

  if (mounted > 1) {
    mountedBySegment.set(segment, mounted - 1)
    return
  }

  mountedBySegment.delete(segment)
  if (!getRouteErrorActive()) emit()
}

/** Test-only: drop everything, so one case cannot leak into the next. */
export function resetRouteErrorPosture(): void {
  mountedBySegment.clear()
  emit()
}
